import { useTranslate } from "@@/translations/use-translate";
import {
    type MutateFunction,
    type MutationOptions,
    type QueryKey,
    type UseMutationResult,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import type { TranslatableString } from "@towni/common";
import {
    isApiError,
    isFunction,
    translatableStringFactory,
} from "@towni/common";
import type { Except } from "type-fest";
import { useFetchClient } from "../fetch-client";

type TanstackMutationOptions<Command, ResponseType = unknown> = Except<
    MutationOptions<ResponseType, unknown, Command>,
    "mutationFn" | "_defaulted"
> & {
    errorCallback?: (errors: (TranslatableString | string)[]) => void;
};

type CustomMutationOptions<
    Command,
    ResponseType = unknown,
> = TanstackMutationOptions<Command, ResponseType> & {
    queryKeysToInvalidate?: QueryKey[] | ((command: Command) => QueryKey[]);
};
/**
 * Custom hook to be wrapped and used for when creating other more
 * specific Tanstack mutation hooks.
 *
 * Tip: Use the added `queryKeysToInvalidate` option to invalidate
 * specific query keys after a successful mutation automatically
 *
 * E.g. check out useAmenityEditMutation.ts and useAmenityAddMutation.ts
 *
 * @template Command - The data to be posted in the body.
 * @template ResponseType - Optional, if server responds with data after posting.
 * @param {string | ((command: Command) => string)} params.route - url for the mutation to call, could be either a string och a function returning the url as a string
 * @param {"post" | "put" | "delete"} [params.method] - The HTTP method for the mutation.
 * @param {Except<MutationOptions<ResponseType, unknown, Command>, "mutationFn" | "_defaulted"> & { queryKeysToInvalidate?: QueryKey[] }} [params.options] - Additional options for the mutation, provided for customization. For example onSuccess, onError, etc.
 * @returns {UseMutationResult<ResponseType, unknown, Command>} - The result of the mutation.
 */
const useTanstackMutation = <Command, ResponseType = unknown>(params: {
    route: string | ((command: Command) => string);
    method?: "post" | "put" | "delete";
    options?: CustomMutationOptions<Command, ResponseType>;
}): UseMutationResult<ResponseType, unknown, Command> => {
    const translate = useTranslate();
    const fetchClient = useFetchClient();
    const queryClient = useQueryClient();
    const options = params.options || {};
    const errorCallback = options.errorCallback;
    const mutationFn: MutateFunction<ResponseType, unknown, Command> = async (
        command: Command,
    ) => {
        return await fetchClient[params.method || "post"]<
            Command,
            ResponseType
        >({
            route:
                typeof params.route === "string"
                    ? params.route
                    : params.route(command),
            body: command,
        });
    };

    return useMutation<ResponseType, unknown, Command>({
        mutationFn,
        mutationKey: options.mutationKey,
        ...(options || {}),
        onError: errorCallback
            ? async (error, command, context) => {
                  if (error instanceof Error) errorCallback([error.message]);
                  if (isApiError(error) && error.reason)
                      errorCallback([translatableStringFactory(error.reason)]);
                  else {
                      errorCallback([
                          translate({
                              sv: "Ett okänt fel uppstod",
                              en: "An unknown error occurred",
                          }),
                      ]);
                  }
                  await options.onError?.(error, command, context);
              }
            : options.onError,
        onSuccess: async (data, command, context) => {
            // If queryKeysToInvalidate is provided, invalidate the cache for the
            // provided keys to make sure the new resource is shown
            const queryKeysToInvalidate = isFunction(
                options.queryKeysToInvalidate,
            )
                ? options.queryKeysToInvalidate(command)
                : options.queryKeysToInvalidate;
            for (const queryKey of queryKeysToInvalidate ?? []) {
                await queryClient.invalidateQueries({ queryKey });
            }

            // If a custom onSuccess callback is provided
            // run it after we've finished our default behavior above
            if (options.onSuccess) {
                await options.onSuccess(data, command, context);
            }
        },
    });
};

export { useTanstackMutation };
export type { TanstackMutationOptions };
